/**
 * Single source of truth for the scan + membership BUNDLE view of a cart.
 *
 * The order summaries (mobile ExpandableOrderSummary, PersistentDesktopSidebar)
 * and the add-ons step all need the same four things to render a bundle: is this
 * a bundle at all, the cadence price, the netted pay-now pricing, and the
 * per-line scan override. Computing them in each place drifted (e.g. differing
 * guards) and risked display ≠ charge, so they're derived here once. Membership
 * activation, cart, market/flag gating and payment mode are all read from the
 * stores/hooks so callers pass only the product lists.
 */
import {
  absorbsMembershipFee,
  bundleScanContributionCents,
  isScanPackage,
  isBundleCart,
  computeBundlePricing,
  type BundlePricing,
} from "@/utils/bloodMembershipPricing";
import { useMarket } from "@/hooks/useMarket";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useMembership } from "@/contexts/MembershipContext";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import { useMembershipPlanStore } from "@/stores/membershipPlanStore";
import { useMembershipTiers } from "@/hooks/useMembershipTiers";
import { getCadencePriceChf } from "@/utils/membershipPricing";

// The structural product shape the pricing helpers read (accepts both the rich
// product-step Product and the leaner order-summary Product).
type BundleProduct = Parameters<typeof isScanPackage>[0];

export interface BundleSummary {
  /** CH + flag gate — the whole membership/bundle surface collapses when false. */
  membershipFeatureEnabled: boolean;
  /** A scan cart with the membership chosen (via the add-ons card or path B). */
  isBundleMode: boolean;
  /** The selected cadence tier's annual price, in cents (299 / 499 / 899). */
  cadenceCents: number;
  /** Netted scan + member add-ons + first cadence invoice — null unless a bundle. */
  bundlePricing: BundlePricing | null;
  /**
   * Per-line DISPLAY price override for a scan in bundle mode — the plain member
   * price (original struck through → member price), NOT netted. The −299 netting
   * lives only in `bundlePricing` (the charge path / expectedOneTimeCents); on the
   * summary the blood-bearing scan shows its real member price and the membership's
   * first year is presented as "included" instead of subtracted from the scan line.
   * A scan that does NOT absorb the fee (Core) is overridden to its list
   * price, so the line reads as the single price we charge rather than
   * falling through to the summary's generic member-price branch.
   *
   * `undefined` for add-on lines, non-scan lines, or non-bundle carts — pass
   * straight to the summary's renderProductItem override slot. `isAddOn` is
   * required because imaging add-ons are typed 'mri' and would otherwise be
   * mistaken for a scan package.
   */
  scanOverride: (
    product: BundleProduct,
    isAddOn: boolean,
  ) => number | undefined;
  /**
   * True when the scan already includes a blood draw (Advanced / Pro), so the
   * membership's first year is covered by the package baseline — the CHF 299 is
   * NOT added to pay-now (it renews next year). False for Core (no blood draw):
   * the membership adds its blood test net-new and is charged today. Drives the
   * "first year included" vs "charged today" caption on the recurring line.
   */
  membershipFirstYearIncluded: boolean;
}

export function useBundleSummary(
  products: BundleProduct[],
  addOns: BundleProduct[],
): BundleSummary {
  const { market } = useMarket();
  const { data: flags } = useFeatureFlags();
  const membershipFeatureEnabled =
    market === "ch" && (flags?.["membership_v2"] ?? true);

  const { isMember } = useMembership();
  const selectedPaymentMode = useBookingFlowStore((s) => s.selectedPaymentMode);
  const membershipActive = useMembershipPlanStore((s) => s.active);
  const membershipPlan = useMembershipPlanStore((s) => s.plan);
  const membershipTiers = useMembershipTiers().tiers;
  const cadenceCents =
    getCadencePriceChf(membershipPlan, membershipTiers) * 100;

  const cartHasScan = products.some(isScanPackage);
  const isBundleMode =
    membershipFeatureEnabled &&
    isBundleCart({
      cartHasScan,
      membershipActive,
      isMember,
      paymentMode: selectedPaymentMode,
    });

  const scanPackage = isBundleMode
    ? (products.find(isScanPackage) ?? null)
    : null;
  const bundlePricing = scanPackage
    ? computeBundlePricing(scanPackage, addOns, cadenceCents)
    : null;

  // DISPLAY value for a scan line: its bundle contribution — the same number
  // the charge is built from.
  //
  // This used to be the un-netted member price, which made every itemisation
  // disagree with its own total on a fee-absorbing scan (Advanced/Pro): the
  // sidebar listed membership 299 + scan 2'290 and then totalled 2'409,
  // overstating by exactly the netted 299, while the payment modal papered over
  // the same gap by rendering the membership as CHF 0 — a reallocation dressed
  // up as a free membership, and one that only balanced at the baseline
  // cadence (at 4x the lines still said 2'409 against a 3'009 charge).
  //
  // Showing the netted contribution instead makes the lines sum at every
  // cadence, on every surface (Laurent, 2026-08-07). Core is unaffected —
  // bundleScanContributionCents only nets a scan that carries its own blood
  // draw, so Core still shows its member price and keeps earning the discount
  // by adding the membership (Sven, 2026-08-03).
  //
  // `isAddOn` is not optional on purpose. Imaging add-ons (body composition,
  // DEXA, mamma MRI) are typed 'mri' with no blood draw, so they look exactly
  // like Core to isScanPackage — without the caller saying which line it is
  // rendering, they'd be forced to list price and lose the member discount
  // they do earn.
  const scanOverride = (
    product: BundleProduct,
    isAddOn: boolean,
  ): number | undefined => {
    if (isAddOn || !isBundleMode || !isScanPackage(product)) return undefined;
    return bundleScanContributionCents(product);
  };

  const membershipFirstYearIncluded =
    isBundleMode && scanPackage != null && absorbsMembershipFee(scanPackage);

  return {
    membershipFeatureEnabled,
    isBundleMode,
    cadenceCents,
    bundlePricing,
    scanOverride,
    membershipFirstYearIncluded,
  };
}
